/**
 * Client-side JPEG/PNG downscale + recompress utility.
 *
 * Bug R20 fix (Iter-68). Kubernetes ingress rejects large uploads with a
 * 413 well before the FastAPI backend sees them (nginx-ingress
 * proxy-body-size defaults to 1MB and the multipart overhead can push a
 * ~500KB image over the edge for some prod deployments). Compress every
 * image client-side to comfortably under 900KB so gallery uploads never
 * hit the proxy ceiling.
 *
 * @param {File}   file       Original image file from an <input type=file>
 * @param {Object} [opts]
 * @param {number} [opts.maxDimension=1600]  Longest edge in pixels.
 * @param {number} [opts.targetKb=900]       Target output size (KB).
 * @param {number} [opts.minQuality=0.55]    Floor for JPEG quality.
 * @returns {Promise<File>} A new File — same name (extension coerced to .jpg
 *   when we transcoded) — with `.type = image/jpeg`.
 */
export async function compressImage(file, opts = {}) {
  const {
    maxDimension = 1600,
    targetKb = 900,
    minQuality = 0.55,
  } = opts;

  // Small files skip work.
  if (file.size <= targetKb * 1024 && file.type !== 'image/heic' && file.type !== 'image/heif') {
    return file;
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });

  let { width, height } = img;
  const longest = Math.max(width, height);
  if (longest > maxDimension) {
    const scale = maxDimension / longest;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // Try successively lower quality until we hit the target size (or the floor).
  let quality = 0.9;
  let blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  while (blob && blob.size > targetKb * 1024 && quality > minQuality) {
    quality -= 0.1;
    blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  }
  if (!blob) return file; // toBlob failed; return original as best-effort

  // Preserve the original stem but force a .jpg extension since we
  // re-encoded as JPEG.
  const stem = (file.name || 'image').replace(/\.[^.]+$/, '');
  return new File([blob], `${stem}.jpg`, { type: 'image/jpeg' });
}
